import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { api } from '../services/api';
import type { BackupStatus, ValidatorStatus } from '../types';
import { format, formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import clsx from 'clsx';

interface BackupTableProps {
  projectId: string;
}

function formatFileSize(bytes: string): string {
  const size = parseInt(bytes, 10);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const statusConfig: Record<BackupStatus, { icon: typeof CheckCircle; color: string; label: string }> = {
  COMPLETED: { icon: CheckCircle, color: 'text-green-500', label: 'Fertig' },
  FAILED: { icon: XCircle, color: 'text-red-500', label: 'Fehlgeschlagen' },
  PENDING: { icon: Clock, color: 'text-yellow-500', label: 'Ausstehend' },
  IN_PROGRESS: { icon: Loader2, color: 'text-blue-500', label: 'Läuft' },
};

const validatorConfig: Record<ValidatorStatus, { color: string; label: string }> = {
  PASSED: { color: 'bg-green-100 text-green-700', label: 'Bestanden' },
  FAILED: { color: 'bg-red-100 text-red-700', label: 'Fehlgeschlagen' },
  PENDING: { color: 'bg-gray-100 text-gray-700', label: 'Ausstehend' },
  RUNNING: { color: 'bg-blue-100 text-blue-700', label: 'Läuft' },
  SKIPPED: { color: 'bg-gray-100 text-gray-700', label: 'Übersprungen' },
};

export default function BackupTable({ projectId }: BackupTableProps) {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['backups', projectId, page],
    queryFn: () => api.backups.list(projectId, page, 10),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data || data.backups.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Keine Backups vorhanden
      </div>
    );
  }

  return (
    <>
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Status
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Dateiname
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Größe
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Erstellt
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Ausgelöst durch
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              Validator
            </th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {data.backups.map((backup) => {
            const status = statusConfig[backup.status];
            const StatusIcon = status.icon;
            const validator = backup.validatorStatus
              ? validatorConfig[backup.validatorStatus]
              : null;

            return (
              <tr key={backup.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <StatusIcon
                      className={clsx(
                        'w-5 h-5',
                        status.color,
                        backup.status === 'IN_PROGRESS' && 'animate-spin'
                      )}
                    />
                    <span className="text-sm">{status.label}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-mono text-gray-700">{backup.filename}</span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {formatFileSize(backup.fileSize)}
                </td>
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm">
                      {format(new Date(backup.createdAt), 'dd.MM.yyyy HH:mm')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(backup.createdAt), {
                        addSuffix: true,
                        locale: de,
                      })}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={clsx(
                    'inline-block px-2 py-1 text-xs font-medium rounded',
                    backup.triggeredBy === 'manual'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  )}>
                    {backup.triggeredBy === 'manual' ? 'Manuell' : 'Geplant'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {validator ? (
                    <span
                      className={clsx(
                        'inline-block px-2 py-1 text-xs font-medium rounded',
                        validator.color
                      )}
                    >
                      {validator.label}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between p-4 border-t">
          <p className="text-sm text-gray-600">
            Seite {page} von {data.pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              Zurück
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= data.pagination.totalPages}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              Weiter
            </button>
          </div>
        </div>
      )}
    </>
  );
}
